import React from 'react';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';

// pastel base + darker selected tone per derived oep status. keeps the color
// system in sync with CarrierDetailStates' "all available states" grid so the
// same status reads the same way across the page.
const STATUS_STYLES = {
  Added: {
    base: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
    selected: 'bg-green-300 text-green-900 border-green-400'
  },
  Updated: {
    base: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200',
    selected: 'bg-amber-300 text-amber-900 border-amber-400'
  },
  Removed: {
    base: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
    selected: 'bg-red-300 text-red-900 border-red-400'
  }
};

/**
 * clickable state chip in the OEP Updates panel. color reflects the derived
 * status (Added = green, Updated = amber, Removed = red) rather than the raw
 * subform Type, so agents read the impact at a glance.
 * @param stateCode - 2-letter state abbreviation to render
 * @param status - derived status label from carrierFactory
 * @param stateName - full state name used as the native tooltip
 * @param isSelected - whether this chip is the one whose counties are open
 * @param onClick - fired when the chip is activated
 */
export default function StateUpdateChip({
  stateCode,
  status,
  stateName,
  isSelected,
  onClick
}) {
  const tone = STATUS_STYLES[status] || STATUS_STYLES.Updated;
  const tooltip = stateName ? `${stateName} — ${status}` : status;

  return (
    <button
      type='button'
      onClick={onClick}
      title={tooltip}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm font-semibold select-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        isSelected ? tone.selected : tone.base
      )}
    >
      {stateCode}
    </button>
  );
}

StateUpdateChip.propTypes = {
  stateCode: propTypes.string.isRequired,
  status: propTypes.oneOf(['Added', 'Updated', 'Removed']).isRequired,
  stateName: propTypes.string,
  isSelected: propTypes.bool,
  onClick: propTypes.func
};
